import { useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Button, Textarea } from '@artstorefronts/ui';

import { COMPLETE_CONTENT_REVIEW_REQUEST_MUTATION } from '../../graphql/mutations';

interface CompleteReviewRequestDialogProps {
  requestId: string;
  userLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
}

function CompleteReviewRequestDialog({
  requestId,
  userLabel,
  open,
  onOpenChange,
  onCompleted,
}: CompleteReviewRequestDialogProps) {
  const { t } = useTranslation('admin');
  const client = useApolloClient();
  const [message, setMessage] = useState('');

  const [completeRequest, { loading, error }] = useMutation(COMPLETE_CONTENT_REVIEW_REQUEST_MUTATION, {
    onCompleted: async () => {
      await client.refetchQueries({ include: ['ContentReviewRequests'] });
      onCompleted?.();
      handleOpenChange(false);
    },
  });

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setMessage('');
    }
  };

  const handleSubmit = () => {
    completeRequest({
      variables: {
        id: requestId,
        message: message.trim() || null,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('moderation.review-requests.complete-dialog.title', { name: userLabel })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-gray-700">{t('moderation.review-requests.complete-dialog.body')}</p>
          <label className="block text-sm font-medium text-gray-800">
            {t('moderation.review-requests.complete-dialog.message-label')}
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('moderation.review-requests.complete-dialog.message-placeholder')}
              rows={4}
              className="mt-1"
              disabled={loading}
            />
          </label>
          {error && <p className="text-sm text-red-600">{t('moderation.review-requests.complete-dialog.error')}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
            {t('moderation.review-requests.complete-dialog.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading
              ? t('moderation.review-requests.complete-dialog.submitting')
              : t('moderation.review-requests.complete-dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CompleteReviewRequestDialog;
